import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { StatusBadge } from '../components/StatusBadge';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell,
} from 'recharts';
import {
  TrendingUp, ChevronRight, Clock, AlertTriangle, ArrowRight, FileText, CalendarSearch, GraduationCap,
} from 'lucide-react';
import { displayToIso } from '../components/ClassMappingManager';

const CHART = ['#1D4ED8', '#0F766E', '#C89B3C', '#7C3AED', '#EA580C', '#16A34A', '#DC2626', '#64748B'];

function EligibilityChecker({ sessions, currentSession }) {
  const [dob, setDob] = useState('');
  const [session, setSession] = useState(currentSession || '');
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const check = async () => {
    setError(null); setResult(null);
    const iso = displayToIso(dob);
    if (!iso) { setError('Enter Date of Birth as DD/MM/YYYY (e.g. 01/04/2019).'); return; }
    setBusy(true);
    try {
      const r = await api.get('/class-mappings/eligible', { params: { dob: iso, session } });
      setResult(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not check eligibility.');
    } finally { setBusy(false); }
  };

  return (
    <section data-testid="eligibility-checker" className="rounded-2xl border border-[#E2E8F0] bg-white p-5">
      <div className="mb-3 flex items-center gap-2">
        <CalendarSearch size={18} className="text-action" />
        <h2 className="font-heading text-lg font-bold text-[#172033]">Class Eligibility Checker</h2>
      </div>
      <div className="grid gap-3 sm:grid-cols-[1fr_1fr_auto] sm:items-end">
        <label className="block">
          <span className="mb-1 block text-xs font-semibold text-[#475569]">Date of Birth (DD/MM/YYYY)</span>
          <input data-testid="eligibility-dob" className="w-full rounded-lg border border-[#E2E8F0] px-3 py-2 text-sm outline-none focus:border-action" placeholder="01/04/2019" value={dob} onChange={(e) => setDob(e.target.value)} />
        </label>
        <label className="block">
          <span className="mb-1 block text-xs font-semibold text-[#475569]">Academic Session</span>
          <select data-testid="eligibility-session" className="w-full rounded-lg border border-[#E2E8F0] px-3 py-2 text-sm outline-none focus:border-action" value={session} onChange={(e) => setSession(e.target.value)}>
            <option value="">All sessions</option>
            {(sessions || []).map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </label>
        <button data-testid="eligibility-check-btn" onClick={check} disabled={busy} className="min-h-[40px] rounded-lg bg-navy px-5 py-2 text-sm font-semibold text-white hover:bg-navy-dark disabled:opacity-60">{busy ? 'Checking…' : 'Check Eligibility'}</button>
      </div>
      {error && <p data-testid="eligibility-error" className="mt-3 rounded-lg bg-[#FEF2F2] p-2.5 text-xs font-semibold text-[#B91C1C]">{error}</p>}
      {result && (result.eligible ? (
        <div data-testid="eligibility-result" className="mt-3 rounded-lg border border-[#BBF7D0] bg-[#F0FDF4] p-3.5">
          <div className="flex items-center gap-2"><GraduationCap size={16} className="text-[#16A34A]" /><span className="font-heading text-lg font-bold text-[#166534]">{result.eligible}</span></div>
          <p className="mt-1.5 text-xs text-[#475569]">Configured DOB range: <b>{result.mapping.dob_from.split('-').reverse().join('/')} – {result.mapping.dob_to.split('-').reverse().join('/')}</b></p>
          <p className="text-xs text-[#475569]">Session: <b>{result.mapping.session || 'All sessions'}</b></p>
          <p className="mt-1.5 text-xs text-[#166534]">{result.reason}</p>
        </div>
      ) : (
        <p data-testid="eligibility-none" className="mt-3 rounded-lg bg-[#FEF3C7] p-3 text-xs font-semibold text-[#92400E]">No eligible class configured for this DOB/session.</p>
      ))}
    </section>
  );
}

const KPIS = [
  { key: 'total_enquiries', label: 'Total Enquiries', color: '#1D4ED8', filter: {} },
  { key: 'forms_issued', label: 'Forms Issued', color: '#4F46E5', filter: { form_issued: 'true' } },
  { key: 'active', label: 'Active Applications', color: '#0F766E', filter: {} },
  { key: 'tests_pending', label: 'Tests Pending', color: '#7C3AED', filter: { status: 'Test Scheduled' } },
  { key: 'tests_scheduled', label: 'Tests Scheduled', color: '#9333EA', filter: { status: 'Test Scheduled' } },
  { key: 'tests_completed', label: 'Tests Completed', color: '#0F766E', filter: { status: 'Test Completed' } },
  { key: 'passed', label: 'Passed', color: '#16A34A', filter: { test_result: 'Pass' } },
  { key: 'failed', label: 'Failed', color: '#DC2626', filter: { status: 'Fail' } },
  { key: 'documents_pending', label: 'Documents Pending', color: '#EA580C', filter: { status: 'Documents Under Review' } },
  { key: 'documents_awaiting_verification', label: 'Docs Awaiting Verification', color: '#F97316', filter: { status: 'Documents Under Review' } },
  { key: 'today_admissions', label: "Today's Admissions", color: '#0EA5E9', filter: {} },
  { key: 'tests_today', label: 'Tests Today', color: '#9333EA', filter: { status: 'Test Scheduled' } },
  { key: 'messages_pending', label: 'Messages Pending', color: '#D97706', filter: {} },
  { key: 'messages_failed', label: 'Messages Failed', color: '#DC2626', filter: {} },
  { key: 'counselling_pending', label: 'Counselling Pending', color: '#D97706', filter: { status: 'Counselling Pending' } },
  { key: 'ready_for_rector', label: 'Ready for Rector', color: '#0F766E', filter: { status: 'Counselling Complete' } },
  { key: 'rector_pending', label: 'Rector Approval Pending', color: '#7C3AED', filter: { status: 'Awaiting Rector Approval' } },
  { key: 'approved', label: 'Approved', color: '#16A34A', filter: { status: 'Approved' } },
  { key: 'rejected', label: 'Rejected', color: '#DC2626', filter: { status: 'Rejected' } },
  { key: 'completed', label: 'Admission Completed', color: '#15803D', filter: { status: 'Admission Completed' } },
];

const toQuery = (f) => new URLSearchParams(f).toString();

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [funnel, setFunnel] = useState([]);
  const [actions, setActions] = useState([]);
  const [classWise, setClassWise] = useState([]);
  const [queue, setQueue] = useState([]);
  const [settings, setSettings] = useState(null);

  const scope = {};

  useEffect(() => {
    api.get('/dashboard/stats', { params: scope }).then((r) => setStats(r.data));
    api.get('/dashboard/funnel', { params: scope }).then((r) => setFunnel(r.data));
    api.get('/dashboard/action-required', { params: scope }).then((r) => setActions(r.data));
    api.get('/dashboard/class-wise').then((r) => setClassWise(r.data));
    api.get('/settings').then((r) => setSettings(r.data)).catch(() => {});
    if (user.role === 'rector') {
      api.get('/cases', { params: { status: 'Awaiting Rector Approval' } }).then((r) => setQueue(r.data));
    }
  }, [user.role]); // eslint-disable-line

  const go = (filter) => navigate(`/applicants?${toQuery(filter)}`);
  const maxFunnel = Math.max(...funnel.map((f) => f.count), 1);

  const title = { rector: 'Management Dashboard', office: 'Office Dashboard' }[user.role];
  const subtitle = { rector: 'Decisions, pipeline and analytics at a glance', office: 'Operations, documents and pending actions' }[user.role];

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div>
        <p className="text-xs font-semibold uppercase tracking-wider text-[#64748B]">Holy Palace Multipurpose Institute</p>
        <h1 className="font-heading text-2xl font-bold text-[#172033] sm:text-3xl">{title}</h1>
        <p className="mt-1 text-sm text-[#64748B]">{subtitle}</p>
      </div>

      {/* Rector priority queue */}
      {user.role === 'rector' && (
        <section className="rounded-2xl border-2 border-gold/40 bg-gradient-to-br from-white to-gold/5 p-5" data-testid="rector-pending-queue">
          <div className="mb-4 flex items-center gap-2">
            <FileText size={18} className="text-gold" />
            <button data-testid="pending-approvals-counter" onClick={() => navigate('/approvals')} className="flex items-center gap-2 text-left">
              <h2 className="font-heading text-lg font-bold text-navy hover:underline">Pending Approvals</h2>
              <span className="rounded-full bg-navy px-2 py-0.5 text-xs font-bold text-white">{queue.length}</span>
            </button>
            <button data-testid="view-all-approvals" onClick={() => navigate('/approvals')} className="ml-auto flex items-center gap-1 text-xs font-semibold text-action hover:underline">View all <ArrowRight size={13} /></button>
          </div>
          {queue.length === 0 ? (
            <p className="text-sm text-[#64748B]">No applications awaiting your approval right now.</p>
          ) : (
            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
              {queue.map((c) => (
                <div key={c.case_id} className="rounded-xl border border-[#E2E8F0] bg-white p-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="font-semibold text-[#172033]">{c.applicant_name}</p>
                      <p className="text-xs text-[#64748B]">{c.case_number} · {c.class_name}</p>
                    </div>
                    <StatusBadge status={c.status} size="sm" />
                  </div>
                  <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-[#475569]">
                    <span>Test: <b>{(c.test?.result?.result) || 'N/A'}</b></span>
                    <span>Rec: <b>{c.office_recommendation || '—'}</b></span>
                  </div>
                  <button
                    data-testid={`view-application-${c.case_number}`}
                    onClick={() => navigate(`/cases/${c.case_id}`)}
                    className="mt-3 flex w-full items-center justify-center gap-1.5 rounded-lg bg-navy py-2 text-sm font-semibold text-white hover:bg-navy-dark"
                  >
                    View Application <ArrowRight size={15} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </section>
      )}

      {/* Class Eligibility Checker (Office + Rector) */}
      <EligibilityChecker sessions={settings?.sessions} currentSession={settings?.current_session} />

      {/* KPI grid */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4" data-testid="kpi-grid">
        {stats && KPIS.map((k) => (
          <button
            key={k.key}
            data-testid={`kpi-${k.key}`}
            onClick={() => go(k.filter)}
            className="group rounded-xl border border-[#E2E8F0] bg-white p-4 text-left transition-all hover:-translate-y-0.5 hover:border-action hover:shadow-md"
          >
            <div className="flex items-center justify-between">
              <span className="h-2 w-2 rounded-full" style={{ backgroundColor: k.color }} />
              <ChevronRight size={15} className="text-[#CBD5E1] group-hover:text-action" />
            </div>
            <p className="mt-3 font-heading text-3xl font-bold text-[#172033]">{stats[k.key]}</p>
            <p className="mt-0.5 text-xs font-medium text-[#64748B]">{k.label}</p>
          </button>
        ))}
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Funnel */}
        <section className="lg:col-span-2 rounded-2xl border border-[#E2E8F0] bg-white p-5">
          <div className="mb-4 flex items-center gap-2">
            <TrendingUp size={18} className="text-action" />
            <h2 className="font-heading text-lg font-bold text-[#172033]">Admission Funnel</h2>
          </div>
          <div className="space-y-2.5">
            {funnel.map((f, i) => (
              <div key={f.label} className="flex items-center gap-3">
                <span className="w-32 shrink-0 text-right text-xs font-medium text-[#475569]">{f.label}</span>
                <div className="relative h-8 flex-1 overflow-hidden rounded-lg bg-[#F1F5F9]">
                  <div
                    className="flex h-full items-center rounded-lg pl-3 text-xs font-bold text-white transition-all"
                    style={{ width: `${Math.max((f.count / maxFunnel) * 100, 6)}%`, backgroundColor: CHART[i % CHART.length] }}
                  >
                    {f.count}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Action required */}
        <section className="rounded-2xl border border-[#E2E8F0] bg-white p-5" data-testid="action-required">
          <div className="mb-3 flex items-center gap-2">
            <AlertTriangle size={18} className="text-[#EA580C]" />
            <h2 className="font-heading text-lg font-bold text-[#172033]">Action Required</h2>
          </div>
          <div className="space-y-1">
            {actions.filter((a) => a.count > 0).length === 0 && <p className="text-sm text-[#64748B]">All clear — no pending bottlenecks.</p>}
            {actions.filter((a) => a.count > 0).map((a) => (
              <button
                key={a.key}
                data-testid={`action-${a.key}`}
                onClick={() => go(a.filter)}
                className="flex w-full items-center justify-between gap-2 rounded-lg px-3 py-2 text-left hover:bg-[#F8FAFC]"
              >
                <span className="text-sm text-[#475569]">{a.label}</span>
                <span className="flex h-6 min-w-6 items-center justify-center rounded-full bg-[#FFEDD5] px-2 text-xs font-bold text-[#EA580C]">{a.count}</span>
              </button>
            ))}
          </div>
        </section>
      </div>

      {/* Class-wise */}
      <section className="rounded-2xl border border-[#E2E8F0] bg-white p-5" data-testid="class-wise">
        <h2 className="mb-4 font-heading text-lg font-bold text-[#172033]">Class-wise Overview</h2>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={200}>
            <BarChart data={classWise} margin={{ top: 8, right: 8, left: -18, bottom: 0 }}>
              <XAxis dataKey="class_name" tick={{ fontSize: 11, fill: '#64748B' }} interval={0} angle={-25} textAnchor="end" height={54} />
              <YAxis tick={{ fontSize: 11, fill: '#64748B' }} allowDecimals={false} />
              <Tooltip contentStyle={{ borderRadius: 10, border: '1px solid #E2E8F0', fontSize: 12 }} />
              <Bar dataKey="enquiries" radius={[4, 4, 0, 0]}>
                {classWise.map((_, i) => <Cell key={i} fill={CHART[i % CHART.length]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </section>
    </div>
  );
}
